import { useEffect, useState } from "react";
import Template from "../app/Template";
import Header from "../layout/Header";
import { useAppDispatch, useAppSelector } from "../../hooks";
import SpaceFiller from "../layout/SpaceFiller";
import Spinner from "../layout/Spinner";
import Container from "../layout/Container";
import NoContent from "../layout/NoContent";
import {
  ExclamationCircleIcon,
  TrashIcon,
  ClockIcon,
  SquaresPlusIcon,
  CloudArrowDownIcon,
  SparklesIcon,
} from "@heroicons/react/24/solid";
import Button from "../layout/Button";
import getButtonStyle from "../../utils/styles";
import { addAlert } from "../../slices/alert";
import { generateAlertText } from "../../utils/boilerplate";
import ActionButtons from "../layout/ActionButtons";
import { translateText } from "../../utils/translation";
import { PlusCircleIcon } from "@heroicons/react/24/outline";
import Modal from "../layout/Modal";
import { Dialog } from "@headlessui/react";
import Card from "../layout/Card";
import {
  NewsletterState,
  createNewsletter,
  deleteNewsletters,
  getNewsletters,
  updateNewsletter,
} from "../../slices/content";
import SwitchToggle from "../forms/SwitchToggle";
import DangerZone from "../forms/DangerZone";
import Indicator from "../layout/Indicator";
import UploadImageDropzone from "../forms/UploadImageDropzone";
import {
  removePhotoFromUploads,
  resetImageUploads,
} from "../../slices/uploads";
import { v4 as uuidv4 } from "uuid";
import HorizontalRadio from "../forms/HorizontalRadio";
import { CSVLink } from "react-csv";
import UploadDocumentDropzone from "../forms/UploadDocumentDropzone";
import { replaceAll } from "../../utils/text";
import TextInputMultiLanguage from "../forms/TextInputMultiLanguage";

export default function Newsletters() {
  const dispatch = useAppDispatch();

  // Fetch data from server
  useEffect(() => {
    dispatch(getNewsletters());
  }, [dispatch]);

  // Set state
  const [selectedNewsletter, setSelectedNewsletter] = useState<{
    id: string;
    name: { [key: string]: string };
    status: string;
    sections: Array<{
      pos: number;
      header: { [key: string]: string };
      body: { [key: string]: string };
      image: { id: string; source: string; data: string } | null;
    }>;
  }>({
    id: "",
    name: { EN: "", FR: "", IT: "", DE: "" },
    status: "A",
    sections: [],
  });
  const [createNewsletterForm, setCreateNewsletterForm] = useState<{
    name: { [key: string]: string };
    status: string;
    build_type: string;
    sections: Array<{
      pos: number;
      header: { [key: string]: string };
      body: { [key: string]: string };
      image: string | null;
    }>;
  }>({
    name: { EN: "", FR: "", IT: "", DE: "" },
    status: "A",
    build_type: "custom",
    sections: [
      {
        pos: 0,
        header: { EN: "", FR: "", IT: "", DE: "" },
        body: { EN: "", FR: "", IT: "", DE: "" },
        image: null,
      },
    ],
  });

  const [showCreateModal, setShowCreateModal] = useState<boolean>(false);
  const [showEditModal, setShowEditModal] = useState<boolean>(false);
  const [showActivatedSpreadsheet, setShowActivatedSpreadsheet] =
    useState<boolean>(false);
  const [currentSpreadsheetId, setCurrentSpreadsheetId] = useState(null);

  // Extract data from Redux store
  const { user } = useAppSelector((state) => state.auth);
  const { newsletters } = useAppSelector((state) => state.content);
  const {
    images: { newsletters: newsletterUploads },
    spreadsheets: { newsletters: newsletterSpreadsheets },
  } = useAppSelector((state) => state.uploads);
  const language = user === null ? "EN" : user.language;

  // Functions
  function selectNewsletterToEdit(e: any, args: any[]) {
    e.preventDefault();
    const [newsletterId] = args;
    const newsletterData = newslettersData.filter(
      (newsletter) => newsletter.id === newsletterId,
    )[0];

    setSelectedNewsletter({
      id: newsletterData.id,
      name: newsletterData.name,
      status: newsletterData.status[0],
      sections: newsletterData.sections.map((x: any) => ({
        pos: x.pos,
        header: x.header,
        body: x.body,
        image: x.image,
      })),
    });
    setShowEditModal(true);
  }

  function openCreateNewsletterModal(e: any) {
    e.preventDefault();
    setShowCreateModal(true);
  }

  async function confirmCreateNewsletter(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    const { name, status, sections } = createNewsletterForm;

    // Validate text fields
    if (name[language] === "") {
      dispatch(
        addAlert(generateAlertText("missing_name", language), "danger", 5000),
      );
      return;
    }

    // Validate sections
    if (sections.length === 0) {
      dispatch(
        addAlert(
          generateAlertText("missing_sections", language),
          "danger",
          5000,
        ),
      );
      return;
    }
    for (let i = 0; i < sections.length; i += 1) {
      if (sections[i].pos !== i) {
        dispatch(
          addAlert(
            generateAlertText("section_position_incorrect", language),
            "danger",
            5000,
          ),
        );
        return;
      }
      if (sections[i].header[language] === "") {
        dispatch(
          addAlert(
            generateAlertText("missing_header", language),
            "danger",
            5000,
          ),
        );
        return;
      }
      if (sections[i].body[language] === "") {
        dispatch(
          addAlert(generateAlertText("missing_body", language), "danger", 5000),
        );
        return;
      }
    }

    // Build sections
    const sectionsObj = sections.map((section) => {
      let sectionImage = null;
      const validImages = newsletterUploads.data[section.pos];
      if (validImages !== undefined) {
        const imageKeys = Object.keys(validImages);
        const image = validImages[imageKeys[0]];
        if (
          image.loading === false &&
          image.success === true &&
          image.data !== null
        ) {
          sectionImage = {
            source: "processed",
            data: image.data,
          };
        }
      }
      return {
        id: uuidv4(),
        pos: section.pos,
        header: section.header,
        body: section.body,
        image: sectionImage,
      };
    });

    // Validate schedule
    setShowCreateModal(false);
    const res: any = await dispatch(
      createNewsletter({
        name,
        property: user.property.id,
        status,
        sections: sectionsObj,
      }),
    );
    if (res.payload.status === 201) {
      dispatch(
        addAlert(
          generateAlertText("create_newsletter", language),
          "success",
          3000,
        ),
      );
      setCreateNewsletterForm({
        name: { EN: "", FR: "", IT: "", DE: "" },
        status: "A",
        build_type: "custom",
        sections: [
          {
            pos: 0,
            header: { EN: "", FR: "", IT: "", DE: "" },
            body: { EN: "", FR: "", IT: "", DE: "" },
            image: null,
          },
        ],
      });
      dispatch(resetImageUploads("newsletters"));
    } else {
      dispatch(
        addAlert(generateAlertText("create_error", language), "danger", 3000),
      );
      setShowCreateModal(true);
    }
  }

  async function confirmEditNewsletter(e: any) {
    e.preventDefault();
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 5000),
      );
      return;
    }
    const { id, name, status, sections } = selectedNewsletter;

    // Validate text fields
    if (name[language] === "") {
      dispatch(
        addAlert(generateAlertText("missing_name", language), "danger", 5000),
      );
      return;
    }

    // Validate sections
    if (sections.length === 0) {
      dispatch(
        addAlert(
          generateAlertText("missing_sections", language),
          "danger",
          5000,
        ),
      );
      return;
    }
    for (let i = 0; i < sections.length; i += 1) {
      if (sections[i].pos !== i) {
        dispatch(
          addAlert(
            generateAlertText("section_position_incorrect", language),
            "danger",
            5000,
          ),
        );
        return;
      }
      if (sections[i].header[language] === "") {
        dispatch(
          addAlert(
            generateAlertText("missing_header", language),
            "danger",
            5000,
          ),
        );
        return;
      }
      if (sections[i].body[language] === "") {
        dispatch(
          addAlert(generateAlertText("missing_body", language), "danger", 5000),
        );
        return;
      }
    }

    // Build sections
    const selectedObj =
      newsletters.data === null
        ? []
        : newsletters.data.filter((x) => x.id === id);
    const removed = newsletterUploads.removed;

    const sectionsObj = sections.map((section) => {
      let sectionImage = null;

      // Try to populate image with upload
      const validImages = newsletterUploads.data[section.pos];
      if (validImages !== undefined) {
        const imageKeys = Object.keys(validImages);
        const image = validImages[imageKeys[0]];
        if (
          image.loading === false &&
          image.success === true &&
          image.data !== null
        ) {
          sectionImage = {
            source: "processed",
            data: image.data,
          };
        }
      }

      // If saved image exists and is valid, overwrite with saved image
      if (selectedObj.length > 0) {
        const candidateSection = selectedObj[0].sections.filter(
          (x: any) => x.pos === section.pos,
        );
        if (candidateSection.length > 0) {
          if (candidateSection[0].image !== null) {
            if (
              candidateSection[0].image.source === "saved" &&
              !removed.includes(candidateSection[0].image.id)
            ) {
              sectionImage = {
                id: candidateSection[0].image.id,
                source: "saved",
                data: candidateSection[0].image.data,
              };
            }
          }
        }
      }

      let sectionId = uuidv4();
      if (selectedObj.length > 0) {
        sectionId = selectedObj[0].id;
      }
      return {
        id: sectionId,
        pos: section.pos,
        header: section.header,
        body: section.body,
        image: sectionImage,
      };
    });

    // Validate schedule
    setShowEditModal(false);
    const res: any = await dispatch(
      updateNewsletter({
        newsletterId: id,
        name,
        property: user.property.id,
        status,
        sections: sectionsObj,
      }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("update_newsletter", language),
          "success",
          3000,
        ),
      );
      setSelectedNewsletter({
        id: "",
        name: { EN: "", FR: "", IT: "", DE: "" },
        status: "A",
        sections: [],
      });
      dispatch(resetImageUploads("newsletters"));
    } else {
      dispatch(
        addAlert(generateAlertText("update_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  async function confirmDeleteNewsletter(e: any, args: any[]) {
    e.preventDefault();
    const [newsletterIds] = args;
    if (newsletterIds.length === 0) return;
    if (user === null) {
      dispatch(
        addAlert(generateAlertText("missing_user", language), "danger", 3000),
      );
      return;
    }
    if (newsletterIds === null || newsletterIds === undefined) {
      dispatch(
        addAlert(
          generateAlertText("missing_newsletters", language),
          "danger",
          3000,
        ),
      );
      return;
    }
    setShowEditModal(false);
    const res: any = await dispatch(
      deleteNewsletters({ newsletterIds: [newsletterIds] }),
    );
    if (res.payload.status === 200) {
      dispatch(
        addAlert(
          generateAlertText("delete_success", language),
          "success",
          3000,
        ),
      );
    } else {
      dispatch(
        addAlert(generateAlertText("delete_error", language), "danger", 3000),
      );
      setShowEditModal(true);
    }
  }

  function addOrRemoveSectionToNewsletterCreate(e: any, args: any[]) {
    e.preventDefault();
    const [action, sectionId] = args;
    if (action === "add") {
      const currentSections = [...createNewsletterForm.sections];
      const newId = currentSections.length;
      currentSections.push({
        pos: newId,
        header: { EN: "", FR: "", IT: "", DE: "" },
        body: { EN: "", FR: "", IT: "", DE: "" },
        image: null,
      });
      setCreateNewsletterForm({
        ...createNewsletterForm,
        sections: currentSections,
      });
    } else if (action === "remove") {
      const currentSections = [...createNewsletterForm.sections];
      const newSections = currentSections.filter(
        (section) => section.pos !== sectionId,
      );
      setCreateNewsletterForm({
        ...createNewsletterForm,
        sections: newSections,
      });
      const sectionUploads = newsletterUploads.data[sectionId];
      if (sectionUploads === undefined) return;
      const imagesToRemove = Object.keys(sectionUploads);
      if (imagesToRemove)
        for (let i = 0; i < imagesToRemove.length; i += 1) {
          dispatch(
            removePhotoFromUploads(imagesToRemove[i], "newsletters", sectionId),
          );
        }
    }
  }

  function addOrRemoveSectionToNewsletterUpdate(e: any, args: any[]) {
    e.preventDefault();
    const [action, sectionId] = args;
    if (action === "add") {
      const currentSections = [...selectedNewsletter.sections];
      const newId = currentSections.length;
      currentSections.push({
        pos: newId,
        header: { EN: "", FR: "", IT: "", DE: "" },
        body: { EN: "", FR: "", IT: "", DE: "" },
        image: null,
      });
      setSelectedNewsletter({
        ...selectedNewsletter,
        sections: currentSections,
      });
    } else if (action === "remove") {
      const currentSections = [...selectedNewsletter.sections];
      const newSections = currentSections.filter(
        (section) => section.pos !== sectionId,
      );
      setSelectedNewsletter({ ...selectedNewsletter, sections: newSections });
      const sectionUploads = newsletterUploads.data[sectionId];
      if (sectionUploads === undefined) return;
      const imagesToRemove = Object.keys(sectionUploads);
      if (imagesToRemove)
        for (let i = 0; i < imagesToRemove.length; i += 1) {
          dispatch(
            removePhotoFromUploads(imagesToRemove[i], "newsletters", sectionId),
          );
        }
    }
  }

  function getImagesForSection(position: number) {
    if (selectedNewsletter === null) return [];
    if (selectedNewsletter.sections.length === 0) return [];
    const section = selectedNewsletter.sections.filter(
      (x: any) => x.pos === position,
    )[0];
    if (section.image === null) return [];
    const remove = newsletterUploads.removed;
    if (remove.includes(section.image.id)) return [];
    return [{ id: section.image.id, data: section.image.data }];
  }

  function activateSpreadsheetData(fileId: string) {
    let data: any = newsletterSpreadsheets.data[fileId];
    if (data === undefined) return;
    data = data.data;
    if (data === null) return;

    const sections = [];
    for (let i = 0; i < data.length; i += 1) {
      if (i === 0) continue;
      const headerObj: { [key: string]: string } = {
        EN: "",
        FR: "",
        IT: "",
        DE: "",
      };
      headerObj[language] = replaceAll(data[i][1], '"', "");
      const bodyObj: { [key: string]: string } = {
        EN: "",
        FR: "",
        IT: "",
        DE: "",
      };
      bodyObj[language] = replaceAll(data[i][1], '"', "");
      sections.push({
        pos: i - 1,
        header: headerObj,
        body: bodyObj,
        image: null,
      });
    }
    setCreateNewsletterForm({ ...createNewsletterForm, sections: sections });
    setShowActivatedSpreadsheet(true);
  }

  function createSectionHeader(e: any, args: any[], selectedLanguage: string) {
    e.preventDefault();
    const existingValues = [...createNewsletterForm.sections];
    const [index] = args;
    existingValues[index].header[selectedLanguage] = e.target.value;
    setCreateNewsletterForm({
      ...createNewsletterForm,
      sections: existingValues,
    });
  }

  function getCreateSectionHeader(args: any[], selectedLanguage: string) {
    const [index] = args;
    return createNewsletterForm.sections[index].header[selectedLanguage];
  }

  function createSectionBody(e: any, args: any[], selectedLanguage: string) {
    e.preventDefault();
    const existingValues = [...createNewsletterForm.sections];
    const [index] = args;
    existingValues[index].body[selectedLanguage] = e.target.value;
    setCreateNewsletterForm({
      ...createNewsletterForm,
      sections: existingValues,
    });
  }

  function getCreateSectionBody(args: any[], selectedLanguage: string) {
    const [index] = args;
    return createNewsletterForm.sections[index].body[selectedLanguage];
  }

  function updateSectionHeader(e: any, args: any[], selectedLanguage: string) {
    e.preventDefault();
    const [index] = args;
    const updatedValues: any[] = [];
    for (let i = 0; i < selectedNewsletter.sections.length; i++) {
      if (i === index) {
        updatedValues.push({
          ...selectedNewsletter.sections[i],
          header: {
            ...selectedNewsletter.sections[i].header,
            [selectedLanguage]: e.target.value,
          },
        });
      } else {
        updatedValues.push(selectedNewsletter.sections[i]);
      }
    }
    setSelectedNewsletter({
      ...selectedNewsletter,
      sections: updatedValues,
    });
  }

  function getSelectedSectionHeader(args: any[], selectedLanguage: string) {
    const [index] = args;
    return selectedNewsletter.sections[index].header[selectedLanguage];
  }

  function updateSectionBody(e: any, args: any[], selectedLanguage: string) {
    e.preventDefault();
    const [index] = args;
    const updatedValues: any[] = [];
    for (let i = 0; i < selectedNewsletter.sections.length; i++) {
      if (i === index) {
        updatedValues.push({
          ...selectedNewsletter.sections[i],
          body: {
            ...selectedNewsletter.sections[i].body,
            [selectedLanguage]: e.target.value,
          },
        });
      } else {
        updatedValues.push(selectedNewsletter.sections[i]);
      }
    }
    setSelectedNewsletter({
      ...selectedNewsletter,
      sections: updatedValues,
    });
  }

  function getSelectedSectionBody(args: any[], selectedLanguage: string) {
    const [index] = args;
    return selectedNewsletter.sections[index].body[selectedLanguage];
  }

  // Format data and filter by query param tag
  const newslettersData = newsletters.data === null ? [] : newsletters.data;
  const buildTypes = [
    ["custom", translateText("custom_newsletter", language)],
    ["spreadsheet", translateText("csv", language)],
    // ["pdf", translateText("pdf", language)]
  ];

  let showActivationSpreadsheetButton = false;
  if (currentSpreadsheetId !== null) {
    const spreadsheet = newsletterSpreadsheets.data[currentSpreadsheetId];
    if (spreadsheet !== undefined) {
      if (
        spreadsheet.loading === false &&
        spreadsheet.success === true &&
        spreadsheet.data !== null
      ) {
        showActivationSpreadsheetButton = true;
      }
    }
  }

  return (
    <Template>
      {/* Header */}
      <Header
        text={translateText("newsletters_title", language)}
        actions={
          <ActionButtons
            primaryButton={
              <Button
                type="button"
                className={`${getButtonStyle("solid", "info")}`}
                onClick={openCreateNewsletterModal}
              >
                <PlusCircleIcon
                  className={`h-5 w-5 text-white mr-1`}
                  aria-hidden="true"
                />
                {translateText("create_newsletter", language)}
              </Button>
            }
          />
        }
      />

      {/* Create newsletter modal */}
      <Modal
        show={showCreateModal}
        setShow={setShowCreateModal}
        confirmText={translateText("create_newsletter", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmCreateNewsletter}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("create_menu", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Build type */}
              <HorizontalRadio
                label={translateText("build_type", language)}
                small={translateText("build_type_newsletter_subtext", language)}
              >
                <>
                  {buildTypes.map((choice) => (
                    <div key={choice[0]} className="flex items-center">
                      <input
                        id={choice[0]}
                        name="build_type"
                        type="radio"
                        defaultChecked={
                          createNewsletterForm.build_type === choice[0]
                        }
                        className="h-4 w-4 border-gray-300 text-primary focus:ring-primary rounded"
                        value={choice[0]}
                        onChange={(e) =>
                          setCreateNewsletterForm({
                            ...createNewsletterForm,
                            build_type: e.target.value,
                          })
                        }
                      />
                      <label
                        htmlFor={choice[0]}
                        className="ml-3 block text-sm font-medium leading-6 text-black"
                      >
                        {choice[1]}
                      </label>
                    </div>
                  ))}
                </>
              </HorizontalRadio>

              {/* If build type is custom */}
              {createNewsletterForm.build_type === "custom" && (
                <>
                  {/* Name */}
                  <TextInputMultiLanguage
                    id="name"
                    type="text"
                    label={translateText("name", language)}
                    className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                    required={true}
                    showLabel={true}
                    state={createNewsletterForm}
                    stateName="name"
                    onChange={setCreateNewsletterForm}
                    defaultLanguage={language}
                    languageChoices={
                      newslettersData.length === 0
                        ? ["EN", "FR", "IT", "DE"]
                        : Object.keys(newslettersData[0].name)
                    }
                  />

                  {/* Newsletter sections */}
                  {createNewsletterForm.sections.map((value, index) => (
                    <div
                      key={index}
                      className="grid grid-cols-1 gap-y-8 border-t border-light pt-4"
                    >
                      <div>
                        {createNewsletterForm.sections.length > 1 && (
                          <div className="flex justify-start">
                            <Button
                              type="button"
                              className={`${getButtonStyle(
                                "solid",
                                "danger",
                              )} mb-3`}
                              onClick={addOrRemoveSectionToNewsletterCreate}
                              onClickArgs={["remove", value.pos]}
                            >
                              <TrashIcon
                                className={`h-5 w-5 text-white`}
                                aria-hidden="true"
                              />
                            </Button>
                          </div>
                        )}

                        <div className="col-span-full">
                          <div>
                            <label
                              htmlFor={`header-${index}`}
                              className={`mb-3 block text-left text-sm font-medium text-black`}
                            >
                              {`${translateText("section", language)} ${
                                index + 1
                              }`}
                            </label>

                            {/* Header */}
                            <TextInputMultiLanguage
                              id={`header-${index}`}
                              type="text"
                              label=""
                              className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                              required={true}
                              showLabel={false}
                              state={createNewsletterForm}
                              stateName="header"
                              valueHandler={getCreateSectionHeader}
                              valueHandlerArgs={[index]}
                              onChangeHandler={createSectionHeader}
                              onChangeHandlerArgs={[index]}
                              defaultLanguage={language}
                              languageChoices={
                                newslettersData.length === 0
                                  ? ["EN", "FR", "IT", "DE"]
                                  : Object.keys(newslettersData[0].name)
                              }
                            />

                            {/* Body */}
                            <div className="mt-3">
                              <TextInputMultiLanguage
                                id={`body-${index}`}
                                type="textarea"
                                label=""
                                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                required={true}
                                showLabel={false}
                                state={createNewsletterForm}
                                stateName="body"
                                valueHandler={getCreateSectionBody}
                                valueHandlerArgs={[index]}
                                onChangeHandler={createSectionBody}
                                onChangeHandlerArgs={[index]}
                                defaultLanguage={language}
                                languageChoices={
                                  newslettersData.length === 0
                                    ? ["EN", "FR", "IT", "DE"]
                                    : Object.keys(newslettersData[0].name)
                                }
                              />
                            </div>

                            {/* Image upload */}
                            <UploadImageDropzone
                              id={`image-upload-${index}`}
                              className="mt-3"
                              limit={1}
                              page="newsletters"
                              savedImages={getImagesForSection(value.pos)}
                              blockId={value.pos.toString()}
                            />
                          </div>
                        </div>
                      </div>
                    </div>
                  ))}

                  <Button
                    type="button"
                    className={`${getButtonStyle("solid", "primary")}`}
                    onClick={addOrRemoveSectionToNewsletterCreate}
                    onClickArgs={["add", null]}
                  >
                    <PlusCircleIcon
                      className={`h-5 w-5 text-white mr-1`}
                      aria-hidden="true"
                    />
                    {translateText("add_another_section", language)}
                  </Button>
                </>
              )}

              {/* If build type is spreadsheet */}
              {createNewsletterForm.build_type === "spreadsheet" && (
                <>
                  <div className="grid grid-cols-1 gap-2">
                    {/* Download template */}
                    <CSVLink
                      className="flex bg-light/20 rounded-md hover:bg-light/40 px-4 py-2"
                      data={[
                        ["section", "header", "body"],
                        [
                          "1",
                          "This is the first header",
                          "This is the first body",
                        ],
                        [
                          "2",
                          "This is the second header",
                          "This is the second body",
                        ],
                        [
                          "3",
                          "This is the third header",
                          "This is the third body",
                        ],
                      ]}
                      filename="audico_newsletter_template.csv"
                    >
                      <div className="m-auto">
                        <CloudArrowDownIcon
                          className={`h-6 w-6 text-secondary mx-auto`}
                          aria-hidden="true"
                        />
                        <span className="text-secondary text-sm">
                          {translateText("download_template", language)}
                        </span>
                      </div>
                    </CSVLink>

                    {/* Import CSV */}
                    <UploadDocumentDropzone
                      id={`import-csv-newsletter`}
                      className=""
                      limit={1}
                      page="newsletters"
                      dataType="spreadsheet"
                      acceptedFilesObject={{
                        "application/vnd.openxmlformats-officedocument.spreadsheetml.sheet":
                          [".xlsx"],
                        "text/csv": [".csv"],
                      }}
                      blockId={null}
                      setCurrentFileId={setCurrentSpreadsheetId}
                    />

                    {/* Click here to activate imported data */}
                    {showActivationSpreadsheetButton && (
                      <Button
                        type="button"
                        className={`${getButtonStyle("solid", "primary")}`}
                        onClick={(e) => {
                          e.preventDefault();
                          if (currentSpreadsheetId === null) {
                            dispatch(
                              addAlert(
                                generateAlertText(
                                  "missing_spreadsheet",
                                  language,
                                ),
                                "danger",
                                3000,
                              ),
                            );
                            return;
                          }
                          activateSpreadsheetData(currentSpreadsheetId);
                        }}
                      >
                        <SparklesIcon
                          className={`h-5 w-5 text-white mr-2`}
                          aria-hidden="true"
                        />
                        {translateText("activate_imported_data", language)}
                      </Button>
                    )}
                  </div>

                  {showActivatedSpreadsheet && (
                    <>
                      {/* Name */}
                      <TextInputMultiLanguage
                        id="name"
                        type="text"
                        label={translateText("name", language)}
                        className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                        required={true}
                        showLabel={true}
                        state={createNewsletterForm}
                        stateName="name"
                        onChange={setCreateNewsletterForm}
                        defaultLanguage={language}
                        languageChoices={
                          newslettersData.length === 0
                            ? ["EN", "FR", "IT", "DE"]
                            : Object.keys(newslettersData[0].name)
                        }
                      />

                      {/* Newsletter sections */}
                      {createNewsletterForm.sections.map((value, index) => (
                        <div
                          key={index}
                          className="grid grid-cols-1 gap-y-8 border-t border-light pt-4"
                        >
                          <div>
                            {createNewsletterForm.sections.length > 1 && (
                              <div className="flex justify-start">
                                <Button
                                  type="button"
                                  className={`${getButtonStyle(
                                    "solid",
                                    "danger",
                                  )} mb-3`}
                                  onClick={addOrRemoveSectionToNewsletterCreate}
                                  onClickArgs={["remove", value.pos]}
                                >
                                  <TrashIcon
                                    className={`h-5 w-5 text-white`}
                                    aria-hidden="true"
                                  />
                                </Button>
                              </div>
                            )}

                            <div className="col-span-full">
                              <div>
                                <label
                                  htmlFor={`header-${index}`}
                                  className={`mb-3 block text-left text-sm font-medium text-black`}
                                >
                                  {`${translateText("section", language)} ${
                                    index + 1
                                  }`}
                                </label>

                                {/* Header */}
                                <TextInputMultiLanguage
                                  id={`header-${index}`}
                                  type="text"
                                  label=""
                                  className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                  required={true}
                                  showLabel={false}
                                  state={createNewsletterForm}
                                  stateName="header"
                                  valueHandler={getCreateSectionHeader}
                                  valueHandlerArgs={[index]}
                                  onChangeHandler={createSectionHeader}
                                  onChangeHandlerArgs={[index]}
                                  defaultLanguage={language}
                                  languageChoices={
                                    newslettersData.length === 0
                                      ? ["EN", "FR", "IT", "DE"]
                                      : Object.keys(newslettersData[0].name)
                                  }
                                />

                                {/* Body */}
                                <TextInputMultiLanguage
                                  id={`body-${index}`}
                                  type="textarea"
                                  label=""
                                  className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                                  required={true}
                                  showLabel={false}
                                  state={createNewsletterForm}
                                  stateName="body"
                                  valueHandler={getCreateSectionBody}
                                  valueHandlerArgs={[index]}
                                  onChangeHandler={createSectionBody}
                                  onChangeHandlerArgs={[index]}
                                  defaultLanguage={language}
                                  languageChoices={
                                    newslettersData.length === 0
                                      ? ["EN", "FR", "IT", "DE"]
                                      : Object.keys(newslettersData[0].name)
                                  }
                                />

                                {/* Image upload */}
                                <UploadImageDropzone
                                  id={`image-upload-${index}`}
                                  className="mt-3"
                                  limit={1}
                                  page="newsletters"
                                  savedImages={getImagesForSection(value.pos)}
                                  blockId={value.pos.toString()}
                                />
                              </div>
                            </div>
                          </div>
                        </div>
                      ))}

                      <Button
                        type="button"
                        className={`${getButtonStyle("solid", "primary")}`}
                        onClick={addOrRemoveSectionToNewsletterCreate}
                        onClickArgs={["add", null]}
                      >
                        <PlusCircleIcon
                          className={`h-5 w-5 text-white mr-1`}
                          aria-hidden="true"
                        />
                        {translateText("add_another_section", language)}
                      </Button>
                    </>
                  )}
                </>
              )}
            </div>
          </div>
        </div>
      </Modal>

      {/* Edit newsletter modal */}
      <Modal
        show={showEditModal}
        setShow={setShowEditModal}
        confirmText={translateText("edit_newsletter", language)}
        cancelText={translateText("cancel", language)}
        confirmHandler={confirmEditNewsletter}
      >
        <div>
          <div className="text-center">
            <Dialog.Title
              as="h3"
              className="text-base font-semibold leading-6 text-gray-900"
            >
              {translateText("edit_newsletter", language)}
            </Dialog.Title>
            <div className="my-10 grid grid-cols-1 gap-y-8">
              {/* Name */}
              <TextInputMultiLanguage
                id="name"
                type="text"
                label={translateText("name", language)}
                className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                required={true}
                showLabel={true}
                state={selectedNewsletter}
                stateName="name"
                onChange={setSelectedNewsletter}
                defaultLanguage={language}
                languageChoices={
                  newslettersData.length === 0
                    ? ["EN", "FR", "IT", "DE"]
                    : Object.keys(newslettersData[0].name)
                }
              />

              {/* Newsletter sections */}
              {selectedNewsletter.sections.map((value, index) => (
                <div
                  key={index}
                  className="grid grid-cols-1 gap-y-8 border-t border-light pt-4"
                >
                  <div>
                    {selectedNewsletter.sections.length > 1 && (
                      <div className="flex justify-start">
                        <Button
                          type="button"
                          className={`${getButtonStyle(
                            "solid",
                            "danger",
                          )} mb-3`}
                          onClick={addOrRemoveSectionToNewsletterCreate}
                          onClickArgs={["remove", value.pos]}
                        >
                          <TrashIcon
                            className={`h-5 w-5 text-white`}
                            aria-hidden="true"
                          />
                        </Button>
                      </div>
                    )}

                    <div className="col-span-full">
                      <div>
                        <label
                          htmlFor={`header-${index}`}
                          className={`mb-3 block text-left text-sm font-medium text-black`}
                        >
                          {`${translateText("section", language)} ${index + 1}`}
                        </label>

                        {/* Header */}
                        <TextInputMultiLanguage
                          id={`header-${index}`}
                          type="text"
                          label=""
                          className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                          required={true}
                          showLabel={false}
                          state={selectedNewsletter}
                          stateName="header"
                          valueHandler={getSelectedSectionHeader}
                          valueHandlerArgs={[index]}
                          onChangeHandler={updateSectionHeader}
                          onChangeHandlerArgs={[index]}
                          defaultLanguage={language}
                          languageChoices={
                            newslettersData.length === 0
                              ? ["EN", "FR", "IT", "DE"]
                              : Object.keys(newslettersData[0].name)
                          }
                        />

                        {/* Body */}
                        <div className="mt-3">
                          <TextInputMultiLanguage
                            id={`body-${index}`}
                            type="textarea"
                            label=""
                            className="flex-1 appearance-none rounded-md rounded-l-none border border-secondary/40 border-l-0 bg-light/20 px-3 py-2 text-black placeholder-secondary focus:border-primary focus:bg-light/40 focus:outline-none focus:ring-primary sm:text-sm"
                            required={true}
                            showLabel={false}
                            state={selectedNewsletter}
                            stateName="body"
                            valueHandler={getSelectedSectionBody}
                            valueHandlerArgs={[index]}
                            onChangeHandler={updateSectionBody}
                            onChangeHandlerArgs={[index]}
                            defaultLanguage={language}
                            languageChoices={
                              newslettersData.length === 0
                                ? ["EN", "FR", "IT", "DE"]
                                : Object.keys(newslettersData[0].name)
                            }
                          />
                        </div>

                        {/* Image upload */}
                        <UploadImageDropzone
                          id={`image-upload-${index}`}
                          className="mt-3"
                          limit={1}
                          page="newsletters"
                          savedImages={getImagesForSection(value.pos)}
                          blockId={value.pos.toString()}
                        />
                      </div>
                    </div>
                  </div>
                </div>
              ))}

              <Button
                type="button"
                className={`${getButtonStyle("solid", "primary")}`}
                onClick={addOrRemoveSectionToNewsletterUpdate}
                onClickArgs={["add", null]}
              >
                <PlusCircleIcon
                  className={`h-5 w-5 text-white mr-1`}
                  aria-hidden="true"
                />
                {translateText("add_another_section", language)}
              </Button>

              {/* Status */}
              <SwitchToggle
                id="status"
                label={translateText("status", language)}
                description={translateText(
                  "status_newsletter_subtext",
                  language,
                )}
                showLabel={true}
                toggle={selectedNewsletter.status === "A"}
                state={selectedNewsletter}
                stateName="status"
                handler={setSelectedNewsletter}
                mapping={{ true: "A", false: "I" }}
              />

              {/* Danger zone */}
              <DangerZone
                objectId={selectedNewsletter.id}
                handler={confirmDeleteNewsletter}
                language={language}
              />
            </div>
          </div>
        </div>
      </Modal>

      {/* Loading spinner */}
      {newsletters.loading ? (
        <SpaceFiller>
          <Spinner />
        </SpaceFiller>
      ) : (
        // Page content
        <SpaceFiller verticalPosition="start">
          <Container className="justify-center mt-10">
            {newslettersData.length > 0 ? (
              <>
                <div className="mx-1 mb-1 grid grid-cols-1 sm:grid-cols-2 md:grid-cols-3 lg:grid-cols-4 xl:grid-cols-5 gap-4">
                  {newslettersData.map((newsletter: NewsletterState) => (
                    <a
                      href="#!"
                      key={newsletter.id}
                      onClick={(e) =>
                        selectNewsletterToEdit(e, [newsletter.id])
                      }
                      className="relative"
                    >
                      {/* Newsletter data */}
                      <Card
                        key={newsletter.id}
                        colour="white"
                        className={`hover:ring-1 hover:ring-secondary/50 hover:ring-inset`}
                      >
                        <div className={`border-b border-light pb-2`}>
                          <div className="items-center justify-between">
                            <div>
                              <h3
                                className={`text-base font-semibold leading-6 text-black truncate`}
                              >
                                {newsletter.name[language]}
                              </h3>
                            </div>
                          </div>
                        </div>
                        <div className="pt-2 grid grid-cols-1 space-y-2">
                          <div className="inline-flex text-sm text-secondary">
                            <SquaresPlusIcon
                              className="h-5 w-5 text-info mr-1"
                              aria-hidden="true"
                            />
                            {`${newsletter.sections.length} ${
                              newsletter.sections.length === 1
                                ? translateText("section", language, true)
                                : translateText("sections", language, true)
                            }`}
                          </div>
                          <div className="inline-flex text-sm text-secondary">
                            <ClockIcon
                              className="h-5 w-5 text-secondary mr-1"
                              aria-hidden="true"
                            />
                            {newsletter.created_by.split(" @ ")[1]}
                          </div>
                        </div>
                      </Card>

                      {/* Status indicator */}
                      <Indicator
                        size={3}
                        colour={
                          newsletter.status[0] === "A" ? "success" : "warning"
                        }
                        pulse={false}
                      />
                    </a>
                  ))}
                </div>
              </>
            ) : (
              // No content placeholder
              <NoContent
                text={translateText("no_newsletters", language)}
                subtext={translateText("no_newsletters_subtext", language)}
                icon={
                  <ExclamationCircleIcon
                    className="mx-auto h-12 w-12 text-secondary animate-bounce"
                    aria-hidden="true"
                  />
                }
              />
            )}
          </Container>
        </SpaceFiller>
      )}
    </Template>
  );
}
